import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams, Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";

export default function Verify() {
  const [params] = useSearchParams();
  const { setSession } = useAuth();
  const [state, setState] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const [retrying, setRetrying] = useState(false);
  const ranRef = useRef(false);

  const verify = useCallback(async (isRetry = false) => {
    const token = params.get("token");
    if (!token) {
      setState("error");
      setMessage("Missing token");
      return;
    }
    if (isRetry) setRetrying(true);
    let lastErr;
    // Auto-retry up to 2 times on 502/503/504 (transient prod backend wake-ups)
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const { data } = await api.get(`/auth/verify-magic`, { params: { token } });
        setSession(data.token, data.user);
        setState("success");
        return;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        if (status && status >= 500 && status < 600 && attempt < 2) {
          // Backoff before next try
          await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
          continue;
        }
        break;
      }
    }
    setState("error");
    setMessage(lastErr?.response?.data?.detail || "Could not verify link. Try again or request a new one.");
    setRetrying(false);
  }, [params, setSession]);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    verify();
  }, [verify]);

  // Declarative redirect, fires as soon as state flips to success.
  if (state === "success") return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--tc-bg-soft)]">
      <div className="tc-card p-10 max-w-md w-full text-center">
        {state === "loading" && (
          <>
            <Loader2 className="animate-spin mx-auto text-slate-400" size={36} />
            <h1 className="font-display text-2xl mt-4">Verifying your link…</h1>
            <p className="text-slate-400 text-xs mt-2">Hang tight, this takes a couple of seconds.</p>
          </>
        )}
        {state === "error" && (
          <>
            <AlertTriangle className="mx-auto text-amber-500" size={42} />
            <h1 className="font-display text-2xl mt-3">Link didn't work</h1>
            <p className="text-slate-500 mt-2 text-sm" data-testid="verify-error">{message}</p>
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2">
              <button
                onClick={() => { setState("loading"); ranRef.current = true; verify(true); }}
                data-testid="verify-retry"
                disabled={retrying}
                className="inline-flex items-center gap-1.5 px-4 h-11 rounded-2xl bg-white border-2 border-slate-200 text-slate-700 font-bold text-sm hover:border-slate-300 disabled:opacity-60"
              >
                <RefreshCw size={14} className={retrying ? "animate-spin" : ""} /> Try again
              </button>
              <Link to="/login" data-testid="verify-back-to-login" className="inline-block px-5 h-11 leading-[44px] rounded-2xl bg-tc-ink text-white font-bold text-sm">
                Request a new link
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
